import { queryOptions, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/useToast';
import {
  BUSINESS_ERROR_CODES,
  BUSINESS_LOGO_KEYS,
  BUSINESS_SCALAR_KEYS,
  BUSINESS_VENDOR_IDENTITY_KEY,
  type BusinessAvailabilityStatus,
  type BusinessContractResponse,
  type BusinessContractWire,
  type BusinessDeleteBlockersResponse,
  type BusinessDeleteResponse,
  type BusinessDetailResponse,
  type BusinessDetailWire,
  type BusinessIndexRowWire,
  type BusinessInUseErrorBody,
  type BusinessVendorIdentityInput,
  type CreateBusinessContractInput,
} from '@releve/shared';
import { useOrganization } from '@/contexts/OrganizationContext';
import { indexQueryOptions } from '@/hooks/lib/indexQueryOptions';
import { ApiError, authedDelete, authedGet, authedPatch, authedPost } from '@/lib/api';
import i18n from '@/i18n';

/**
 * Businesses data hooks.
 *
 * WRITES all go through the Hono `businesses` router
 * (`apps/api/src/routes/people/businesses.ts`, `/api/v1/businesses`): create /
 * update (`create_business` / `update_business`, vendor identity in the same
 * transaction), the guarded delete, the delete-blocker counts and contract
 * create. `businesses` and its child tables become backend-write-only
 * (`20260925210100_business_backend_write_lockdown.sql`), and a RESTRICTIVE
 * block on UPDATE / DELETE fails SILENTLY (0 rows) — never write them
 * directly from the browser.
 *
 * READS: the index and the by-code detail go through the backend too.
 * `useBusinesses` (the picker list) is still a direct read — on the
 * burn-down list in docs/BACKEND_API.md.
 *
 * Query keys, all under `['businesses', orgId]` (what every business write
 * invalidates):
 * - index          `['businesses', orgId, 'index', {}]`
 * - detail         `['businesses', orgId, 'detail', code]`
 * - delete guard   `['businesses', orgId, 'delete-blockers', businessId]`
 */
export const businessKeys = {
  all: ['businesses'] as const,
  org: (organizationId: string | undefined) => ['businesses', organizationId] as const,
  detail: (organizationId: string | undefined, code: string | undefined) =>
    ['businesses', organizationId, 'detail', code] as const,
  deleteBlockers: (organizationId: string | undefined, businessId: string | undefined) =>
    ['businesses', organizationId, 'delete-blockers', businessId] as const,
};

/** `/api/v1/businesses/orgs/:orgId/businesses` — the write + detail collection. */
export const businessesBase = (organizationId: string) =>
  `/api/v1/businesses/orgs/${organizationId}/businesses`;

export interface Business {
  id: string;
  organization_id: string;
  name: string;
  code: string;
  contact_email?: string | null;
  contact_phone?: string | null;
  address?: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  business_type_id: string;
  artist_specialties?: string[] | null;
  availability_status?: string | null;
  primary_contact_id?: string | null;
  billing_contact_id?: string | null;
  // Joined fields
  business_types?: {
    id: string;
    code: string;
    name: string;
    description?: string | null;
  };
  // The business's primary contact person. For a "person-business" (a person
  // marked is_business whose linked_business_id points back at this row) the
  // person IS the business, so their email is used when contact_email is blank.
  primary_contact?: {
    id: string;
    email?: string | null;
    is_business?: boolean | null;
    linked_business_id?: string | null;
  } | null;
  // Computed fields (not in database)
  primary_address?: string | null;
  // Top-level lifts of joined data — entity grids' default filter/sort/
  // search read `entity[key]`, so these beat per-grid filterFn workarounds.
  city?: string | null;
  country?: string | null;
  business_type_name?: string | null;
  // Activity totals (transactions / spending / income) are NOT on this row:
  // they are computed server-side by the `business_index` view — read them
  // through useBusinessIndex().
  business_locations?: Array<BusinessLocation>;
}

export interface BusinessLocation {
  id: string;
  location_type: string;
  is_primary: boolean;
  is_active: boolean;
  notes?: string | null;
  gis_locations: {
      id: string;
      formatted_address: string;
      street_number?: string | null;
      street_name?: string | null;
      city?: string | null;
      state_province?: string | null;
      postal_code?: string | null;
      country?: string | null;
      country_code?: string | null;
      latitude: string;
      longitude: string;
      confidence_score?: string | null;
      is_validated?: boolean | null;
    };
}

export const useBusinesses = (organizationId?: string) => {
  return useQuery({
    queryKey: ['businesses', organizationId],
    queryFn: async () => {
      if (!organizationId) return [];

      // Fetch businesses with all locations from business_locations -> gis_locations
      const { data: businessesData, error: businessesError } = await supabase
        .from('businesses')
        .select(`
          *,
          business_types (
            id,
            code,
            name,
            description
          ),
          primary_contact:people!vendors_primary_contact_id_fkey (
            id,
            email,
            is_business,
            linked_business_id
          ),
          business_locations!left (
            id,
            location_type,
            is_primary,
            is_active,
            notes,
            gis_locations (
              id,
              formatted_address,
              street_number,
              street_name,
              city,
              state_province,
              postal_code,
              country,
              country_code,
              latitude,
              longitude,
              confidence_score,
              is_validated
            )
          )
        `)
        .eq('organization_id', organizationId)
        .order('name');

      if (businessesError) throw businessesError;

      // Process businesses and add primary address.
      // Cast through the known row shape: the `primary_contact` embed is a
      // to-one FK (people via primary_contact_id), but the generated relationship
      // is `isOneToOne: false`, so supabase-js mistypes the embed and degrades the
      // whole row type. PostgREST returns the correct to-one object at runtime.
      const businessRows = (businessesData || []) as unknown as Business[];
      const businessesWithAddress = businessRows.map(business => {
        const locations = business.business_locations || [];
        // Find location marked as primary, otherwise use first location
        const primaryLocation = locations.find((loc: BusinessLocation) => loc.is_primary === true);
        const selectedLocation = primaryLocation || locations[0];

        // When this business IS a person (person-business), fall back to the
        // linked person's email so the contact column isn't empty. Guarded on
        // linked_business_id === business.id so a regular business with a
        // contact person doesn't inherit that person's email.
        const pc = business.primary_contact;
        const isPersonBusiness = !!pc?.is_business && pc?.linked_business_id === business.id;
        const resolvedEmail =
          business.contact_email || (isPersonBusiness ? pc?.email ?? null : null);

        return {
          ...business,
          contact_email: resolvedEmail,
          primary_address: selectedLocation?.gis_locations?.formatted_address || null,
          // Grid-friendly top-level fields — EntitiesDataGrid's default
          // filter/sort/search read `entity[key]` directly, so derived
          // fields beat per-grid filterFn workarounds (same pattern as
          // primary_address above).
          city: selectedLocation?.gis_locations?.city || null,
          country: selectedLocation?.gis_locations?.country || null,
          business_type_name: business.business_types?.name || null,
          // Keep business_locations for map view
          business_locations: business.business_locations
        };
      });

      return businessesWithAddress as Business[];
    },
    enabled: !!organizationId,
    // Reference-y data (pickers) — 5 min keeps it responsive across tab
    // switches without refetching on every focus.
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * The COMPLETE org business index (`business_index` view, keyset-paged past
 * PostgREST's 1,000-row cap by GET /api/v1/businesses/orgs/:orgId/index) —
 * the businesses grid and map both read this one query. The Transactions /
 * Spending / Income rollups are computed server-side by the view (the rules
 * `aggregateEntitySpending` used to apply in the browser), never summed here.
 *
 * Liveness contract: key `["businesses", orgId, "index", {}]` nests under
 * the `["businesses", orgId]` prefix the three business mutations,
 * usePersonBusiness, ContactListTable, useAddExpenseFromImport and
 * useLinkToAccountingImport already invalidate. The rollups and the location
 * columns ALSO move on writes outside the businesses domain, so these
 * writers invalidate the bare `["businesses"]` prefix too:
 *  - expense create / update / delete (useExpenses),
 *  - AP bill and AR invoice create / update / delete (useAccountsPayable,
 *    useAccountsReceivable),
 *  - ledger transaction writes (useLedgerTransactions),
 *  - clearing actions (useClearingActions),
 *  - business location add / update / remove / set-primary
 *    (useBusinessLocations),
 *  - logo upload / removal (useImageUpload / useImageDelete).
 * The display_email fallback reads `businesses.primary_contact_id`, which
 * only the person-business link writes (usePersonBusiness, already covered);
 * `business_contacts.is_primary` (BusinessContactsTab) does not feed it.
 * Server-side writers (the recurring-documents pg_cron sweep) are covered by
 * the 60s staleTime. A new writer that moves a business's totals, location
 * or primary contact must add the same invalidation.
 */
export function useBusinessIndex() {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;
  return useQuery(
    indexQueryOptions<BusinessIndexRowWire>({
      entity: 'businesses',
      organizationId: orgId,
      path: `/api/v1/businesses/orgs/${orgId}/index`,
    }),
  );
}

// ---------------------------------------------------------------------------
// Detail
// ---------------------------------------------------------------------------

/**
 * Options for the by-code detail (`GET …/businesses/by-code/:code`). A 404
 * resolves to `null` so the detail page keeps its not-found branch. Shared by
 * {@link useBusinessByCode} and the tab hooks that `select` a slice of it
 * (`useBusinessContacts`), so they all read ONE cache entry.
 */
export function businessDetailQueryOptions(organizationId: string | undefined, code: string | undefined) {
  return queryOptions({
    queryKey: businessKeys.detail(organizationId, code),
    queryFn: async (): Promise<BusinessDetailWire | null> => {
      if (!code || !organizationId) return null;
      try {
        const result = await authedGet<BusinessDetailResponse>(
          `${businessesBase(organizationId)}/by-code/${encodeURIComponent(code)}`,
        );
        return result.business;
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
    enabled: !!code && !!organizationId,
  });
}

/**
 * The full business (`BusinessDetailWire`: the row, its type, vendor identity,
 * addresses and contacts) for the detail page and the edit dialog. The org
 * defaults to the selected organization. Every business write invalidates
 * `['businesses', orgId]`, which covers this key — so a logo upload or an edit
 * refreshes the page without a manual refetch.
 */
export function useBusinessByCode(code?: string, organizationId?: string) {
  const { selectedOrganization } = useOrganization();
  const orgId = organizationId ?? selectedOrganization?.id;
  return useQuery(businessDetailQueryOptions(orgId, code));
}

/**
 * The delete-guard counts (`GET …/businesses/:businessId/delete-blockers`),
 * fetched lazily by the danger zone — pass `enabled: false` until it is
 * shown. Never cached as fresh: the counts gate a destructive action.
 */
export function useBusinessDeleteBlockers(businessId?: string, options?: { enabled?: boolean }) {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;
  return useQuery({
    queryKey: businessKeys.deleteBlockers(orgId, businessId),
    queryFn: () =>
      authedGet<BusinessDeleteBlockersResponse>(`${businessesBase(orgId!)}/${businessId}/delete-blockers`),
    enabled: !!orgId && !!businessId && (options?.enabled ?? true),
    staleTime: 0,
  });
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

/**
 * What the create dialog / FileVatReturnDialog send. Empty strings are fine —
 * the shared schema folds `''` to `null`. `code` blank / absent → the DB
 * generates `VEN-###`. `vendor_identity` is upserted in the SAME transaction
 * (no second request).
 */
export interface CreateBusinessData {
  name: string;
  code?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  address?: string | null;
  business_type_id: string;
  artist_specialties?: string[] | null;
  availability_status?: BusinessAvailabilityStatus | null;
  vendor_identity?: BusinessVendorIdentityInput;
}

/** A PATCH body: any subset. Present keys are written (`null` clears), absent keys untouched. */
export type UpdateBusinessData = Partial<Omit<CreateBusinessData, 'code'>> & {
  /** NOT NULL — a blank code is a 400. Absent = unchanged. */
  code?: string;
  logo_small?: string | null;
  logo_medium?: string | null;
  logo_large?: string | null;
};

type BusinessPayload = Record<string, unknown>;

const CREATE_KEYS: ReadonlySet<string> = new Set<string>([
  ...BUSINESS_SCALAR_KEYS,
  BUSINESS_VENDOR_IDENTITY_KEY,
]);
const UPDATE_KEYS: ReadonlySet<string> = new Set<string>([
  ...BUSINESS_SCALAR_KEYS,
  ...BUSINESS_LOGO_KEYS,
  BUSINESS_VENDOR_IDENTITY_KEY,
]);

/**
 * Keep only the keys the (strict) write schema accepts, dropping `undefined`.
 * A caller that spreads a whole `Business` row (organization_id, created_by,
 * joins…) would otherwise 400.
 */
function toBusinessPayload(data: object, allowed: ReadonlySet<string>): BusinessPayload {
  const payload: BusinessPayload = {};
  for (const [key, value] of Object.entries(data)) {
    if (value === undefined || !allowed.has(key)) continue;
    payload[key] = value;
  }
  return payload;
}

/** The coded envelope a business write carries (`err.envelope.code`), or `null`. */
export function businessErrorCodeOf(error: unknown): string | null {
  const code = (error as { envelope?: { code?: unknown } } | null | undefined)?.envelope?.code;
  return typeof code === 'string' ? code : null;
}

/** The `business_in_use` refusal of a DELETE, with its `reasons` (blocker counts), or `null`. */
export function businessInUseOf(error: unknown): BusinessInUseErrorBody | null {
  if (businessErrorCodeOf(error) !== BUSINESS_ERROR_CODES.inUse) return null;
  return (error as { envelope: BusinessInUseErrorBody }).envelope;
}

/**
 * The coded 409s are user-facing rules, not bugs: App.tsx's MutationCache
 * reads this and keeps Sentry quiet for them (every other mutation error is
 * still captured there — the hooks never capture twice).
 */
export const BUSINESS_MUTATION_META = {
  expectedErrorCodes: Object.values(BUSINESS_ERROR_CODES) as readonly string[],
} as const;

const CODED_TOAST_KEYS: Record<string, string> = {
  [BUSINESS_ERROR_CODES.codeTaken]: 'common:businesses.toast.codeTaken',
  [BUSINESS_ERROR_CODES.inUse]: 'common:businesses.toast.inUse',
  [BUSINESS_ERROR_CODES.locationPrimary]: 'common:businesses.toast.locationPrimary',
  [BUSINESS_ERROR_CODES.contactExists]: 'common:businesses.toast.contactExists',
  [BUSINESS_ERROR_CODES.locationExists]: 'common:businesses.toast.locationExists',
  [BUSINESS_ERROR_CODES.primaryConflict]: 'common:businesses.toast.primaryConflict',
};

/**
 * Toast a failed business write: the coded 409s get their own message, any
 * other failure the `failedKey` headline with the server message as the
 * description. Shared by every business-domain write hook.
 */
export function toastBusinessError(error: Error, failedKey: string) {
  const code = businessErrorCodeOf(error);
  const codedKey = code ? CODED_TOAST_KEYS[code] : undefined;
  if (codedKey) {
    toast.error(i18n.t(codedKey));
    return;
  }
  toast.error(i18n.t(failedKey), error.message ? { description: error.message } : undefined);
}

/**
 * Create a business (POST, `create_business` — atomic with its vendor
 * identity). Call-compatible with the old hook: `{ organizationId, data,
 * userId }` (`userId` is ignored — the RPC stamps `auth.uid()`). Resolves to
 * the created `BusinessDetailWire`, whose `code` is the server's.
 */
export const useCreateBusiness = () => {
  const queryClient = useQueryClient();

  return useMutation({
    meta: BUSINESS_MUTATION_META,
    mutationFn: async ({ organizationId, data }: {
      organizationId: string;
      data: CreateBusinessData;
      /** @deprecated ignored — the server uses the caller's JWT. */
      userId?: string;
    }): Promise<BusinessDetailWire> => {
      const result = await authedPost<BusinessDetailResponse>(
        businessesBase(organizationId),
        toBusinessPayload(data, CREATE_KEYS),
      );
      return result.business;
    },
    onSuccess: (business, { organizationId }) => {
      void queryClient.invalidateQueries({ queryKey: businessKeys.org(organizationId) });
      void queryClient.invalidateQueries({ queryKey: ['people', organizationId] });
      void queryClient.invalidateQueries({ queryKey: ['business-vendor-identity', business.id] });
      toast.success(i18n.t('common:businesses.toast.created', { name: business.name }));
    },
    onError: (error: Error) => toastBusinessError(error, 'common:businesses.toast.createFailed'),
  });
};

/**
 * Update a business (PATCH, `update_business`). Present keys are written,
 * absent keys untouched; a present `vendor_identity` upserts the identity in
 * the same transaction. Call-compatible with the old hook (`{ id, data }`);
 * the org is the selected organization. Resolves to the updated detail.
 */
export const useUpdateBusiness = () => {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.id;

  return useMutation({
    meta: BUSINESS_MUTATION_META,
    mutationFn: async ({ id, data }: { id: string; data: UpdateBusinessData }): Promise<BusinessDetailWire> => {
      if (!organizationId) throw new Error('No organization selected');
      const result = await authedPatch<BusinessDetailResponse>(
        `${businessesBase(organizationId)}/${id}`,
        toBusinessPayload(data, UPDATE_KEYS),
      );
      return result.business;
    },
    onSuccess: (business) => {
      const orgId = business.organization_id ?? organizationId;
      void queryClient.invalidateQueries({ queryKey: businessKeys.org(orgId) });
      // A rename changes person_index.organization_names (people grid liveness).
      void queryClient.invalidateQueries({ queryKey: ['people', orgId] });
      void queryClient.invalidateQueries({ queryKey: ['business-vendor-identity', business.id] });
      toast.success(i18n.t('common:businesses.toast.updated', { name: business.name }));
    },
    onError: (error: Error) => toastBusinessError(error, 'common:businesses.toast.updateFailed'),
  });
};

/**
 * Delete a business. The route answers 409 `business_in_use` (with `reasons`,
 * the blocker counts) when anything references it — read it with
 * {@link businessInUseOf}. The caller decides where to navigate afterwards.
 * The org is the selected organization.
 */
export const useDeleteBusiness = () => {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.id;

  return useMutation({
    meta: BUSINESS_MUTATION_META,
    mutationFn: async (businessId: string): Promise<string> => {
      if (!organizationId) throw new Error('No organization selected');
      await authedDelete<BusinessDeleteResponse>(`${businessesBase(organizationId)}/${businessId}`);
      return businessId;
    },
    onSuccess: (businessId) => {
      // The blockers of a deleted business are meaningless — drop, don't refetch.
      // (Its detail key refetches to a 404, which resolves to null.)
      queryClient.removeQueries({ queryKey: businessKeys.deleteBlockers(organizationId, businessId) });
      void queryClient.invalidateQueries({ queryKey: businessKeys.org(organizationId) });
      // Deleting a business drops its business_contacts → person_index.organization_names.
      void queryClient.invalidateQueries({ queryKey: ['people', organizationId] });
      void queryClient.invalidateQueries({ queryKey: ['business-vendor-identity', businessId] });
      toast.success(i18n.t('common:businesses.toast.deleted'));
    },
    onError: (error: Error) => toastBusinessError(error, 'common:businesses.toast.deleteFailed'),
  });
};

/**
 * Create a contract for a business (POST …/businesses/:businessId/contracts,
 * a plain insert through the backend client). The server pins
 * `organization_id`, `business_id`, `created_by` and `status: 'active'`.
 * Invalidates `['business-contracts', businessId]` and the business prefix
 * (the delete guard counts contracts).
 */
export const useCreateBusinessContract = () => {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.id;

  return useMutation({
    meta: BUSINESS_MUTATION_META,
    mutationFn: async ({ businessId, data }: {
      businessId: string;
      data: CreateBusinessContractInput;
    }): Promise<BusinessContractWire> => {
      if (!organizationId) throw new Error('No organization selected');
      const result = await authedPost<BusinessContractResponse>(
        `${businessesBase(organizationId)}/${businessId}/contracts`,
        data,
      );
      return result.contract;
    },
    onSuccess: (_contract, { businessId }) => {
      void queryClient.invalidateQueries({ queryKey: ['business-contracts', businessId] });
      void queryClient.invalidateQueries({ queryKey: businessKeys.org(organizationId) });
      toast.success(i18n.t('common:businesses.toast.contractCreated'));
    },
    onError: (error: Error) => toastBusinessError(error, 'common:businesses.toast.contractCreateFailed'),
  });
};
